import { useEmployeeSession } from '@features/employee-session';
import { cn } from '@lib/cn';
import { initials } from '@lib/initials';
import { ROLE_LABEL } from '@lib/operator';
import { Link, useLocation } from 'react-router';

import type { Operator } from '@lib/operator';

import { NAV_GROUPS, type NavItem } from './nav-items';
import { visibleNavGroups } from './nav-visibility';

/** Nothing is permitted until the employee session answers, and nothing if it fails. */
const NO_PERMISSIONS: readonly string[] = [];

function isActive(pathname: string, href: string): boolean {
    return pathname === href || pathname.startsWith(`${href}/`);
}

function NavRow({ item, active }: { item: NavItem; active: boolean }) {
    const Icon = item.icon;
    const inner = (
        <>
            <Icon className={cn('size-[18px]', active ? 'opacity-100' : 'opacity-90')} />
            <span>{item.label}</span>
            {item.disabled && (
                <span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-content-faint">
                    Soon
                </span>
            )}
        </>
    );

    const base =
        'flex w-full items-center gap-3 rounded-lg px-2.5 py-2 text-left text-[13.5px] font-medium transition-colors';

    if (item.disabled) {
        return (
            <span
                className={cn(base, 'cursor-not-allowed text-content-faint')}
                aria-disabled="true"
            >
                {inner}
            </span>
        );
    }

    return (
        <Link
            to={item.href}
            className={cn(
                base,
                active
                    ? 'bg-accent-surface font-semibold text-accent-text'
                    : 'text-content-subtle hover:bg-surface-sunken hover:text-content',
            )}
            aria-current={active ? 'page' : undefined}
        >
            {inner}
        </Link>
    );
}

export function Sidebar({ operator }: { operator: Operator }) {
    const { pathname } = useLocation();
    const employeeSession = useEmployeeSession();
    const groups = visibleNavGroups(
        NAV_GROUPS,
        employeeSession.isSuccess ? employeeSession.data.permissions : NO_PERMISSIONS,
    );

    return (
        <nav className="flex h-full flex-col gap-1 p-3" aria-label="Primary">
            {groups.map((group, i) => (
                <div key={group.label ?? `group-${i}`} className="mb-1.5">
                    {group.label && (
                        <div className="px-2.5 pb-1.5 pt-3 text-[11px] font-semibold uppercase tracking-wide text-content-faint">
                            {group.label}
                        </div>
                    )}
                    {group.items.map((item) => (
                        <NavRow
                            key={item.href}
                            item={item}
                            active={isActive(pathname, item.href)}
                        />
                    ))}
                </div>
            ))}

            {/*
             * The signed-in employee, from Atelier's shared session, and the
             * Backoffice tier the API resolved for them (NOXSCRUM-1436).
             *
             * This line used to read "Veevart employee" because no role
             * existed. Now that one does, showing it is what makes a disabled
             * button legible — an operator who cannot flip a flag can see, in
             * one place, that they are read-only rather than that the app is
             * broken.
             */}
            <div className="mt-auto flex items-center gap-2.5 border-t border-line-subtle px-2.5 pt-3">
                <span className="flex size-7 items-center justify-center rounded-full border border-accent-line bg-accent-surface text-[10px] font-semibold text-accent-text">
                    {initials(operator.name)}
                </span>
                <div className="min-w-0 text-xs leading-tight">
                    <div className="truncate font-semibold text-content">{operator.name}</div>
                    <div className="truncate text-content-muted">{ROLE_LABEL[operator.role]}</div>
                </div>
            </div>
        </nav>
    );
}
